// sound-manager.js — audio loading, decoding, and playback for Phase 4.
//
// Responsibilities:
//   - Initialize (and "unlock") an audio output path on first user
//     gesture. Browsers require this — see the splash screen for
//     where that first gesture comes from.
//   - Resolve an item id (e.g. "cow") to its sound + intro URLs via
//     the catalog, fetch, decode, and cache the results.
//   - Expose a Promise-based play(itemId) that resolves when the
//     last audio clip for that press has finished. This shape is
//     deliberately chosen so Phase 5 can:
//       await centerAnimation(button);
//       await sound.play(itemId);
//       await restoreAnimation(button);
//     and can queue rapid taps by chaining promises externally.
//
// Backend abstraction:
//   The module is written against a small Backend interface so the
//   heavy lifting (AudioContext decoding vs. HTMLAudioElement) can
//   be swapped out. Production selects the Web Audio backend when
//   available and falls back to HTMLAudioElement otherwise. Tests
//   inject a mock backend directly so the suite doesn't need
//   browser audio APIs at all.
//
//   interface Backend {
//     unlock(): Promise<void>
//     load(url: string): Promise<AudioHandle>
//     play(handle: AudioHandle): Promise<void>   // resolves on end
//     stop(): void
//   }
//
// Cancellation:
//   A monotonic "playback token" is bumped whenever stop() or a new
//   play() is issued. In-flight sequences check the token after
//   each await boundary and bail out if they've been superseded.
//   This avoids the classic "zombie audio" bug where a late-resolving
//   promise plays its tail clip even though the user has moved on.

import { getItemById, getCategories } from './data/catalog.js';
import { getVerbalDescriptions } from './settings.js';
import { getLocale as getI18nLocale } from './i18n.js';

/**
 * Diagnostic logger. Active whenever `window.__iheSoundDebug === true`,
 * which can be set from the browser console at runtime:
 *
 *   window.__iheSoundDebug = true
 *
 * This exists to trace the audio pipeline when sound silently fails —
 * e.g. the dev-server-vs-static-server discrepancy seen in Phase 5
 * where Safari plays audio under `npm run dev` but not `npx serve dist`.
 * Leave it dormant in normal operation (no performance cost).
 */
function dbg(...args) {
  if (typeof window !== 'undefined' && window.__iheSoundDebug === true) {
    // eslint-disable-next-line no-console
    console.log(...args);
  }
}

/**
 * @typedef {Object} Backend
 * @property {() => Promise<void>} unlock
 * @property {(url: string) => Promise<unknown>} load
 * @property {(handle: unknown) => Promise<void>} play
 * @property {() => void} stop
 */

/**
 * @typedef {Object} SoundManager
 * @property {() => Promise<void>} unlock
 * @property {(itemId: string) => Promise<void>} play
 * @property {() => void} stop
 * @property {(categoryId: string) => Promise<void>} preloadCategory
 * @property {(itemId: string) => Promise<void>} preloadItem
 * @property {() => boolean} isUnlocked
 * @property {() => void} destroy
 */

/**
 * Create a sound manager.
 *
 * @param {{ backend?: Backend }} [opts]
 * @returns {SoundManager}
 */
export function createSoundManager(opts = {}) {
  const backend = opts.backend ?? selectDefaultBackend();

  /** @type {Map<string, { sound: unknown, intro: unknown | null }>} */
  const cache = new Map();
  /** @type {Map<string, Promise<{ sound: unknown, intro: unknown | null }>>} */
  const inflight = new Map();

  let unlocked = false;
  let playbackToken = 0;
  let destroyed = false;

  async function unlock() {
    if (unlocked || destroyed) return;
    await backend.unlock();
    unlocked = true;
  }

  /**
   * Fetch+decode an item's sound (and, when present, its English intro).
   * Missing intro files resolve to null rather than failing — not
   * every item is guaranteed to have a verbal description recorded.
   */
  async function preloadItem(itemId) {
    if (destroyed) return;
    if (cache.has(itemId)) return;
    if (inflight.has(itemId)) {
      await inflight.get(itemId);
      return;
    }

    const item = getItemById(itemId);
    if (!item) {
      throw new Error(`[sound-manager] unknown item id: ${itemId}`);
    }

    const locale = getI18nLocale();
    const soundUrl = item.soundPath;
    const introUrl = item.introPath(locale);

    const load = (async () => {
      const soundP = backend.load(soundUrl);
      // The intro is best-effort: if the file doesn't exist, we still
      // want the animal sound to play without a visible error.
      const introP = backend.load(introUrl).catch(() => null);
      const [sound, intro] = await Promise.all([soundP, introP]);
      const entry = { sound, intro };
      cache.set(itemId, entry);
      return entry;
    })();

    inflight.set(itemId, load);
    try {
      await load;
    } finally {
      inflight.delete(itemId);
    }
  }

  /**
   * Preload every sound (and intro) for a whole category in parallel.
   * Individual failures don't reject the overall promise — metrics/UX
   * reasons: one broken file shouldn't poison the whole page.
   */
  async function preloadCategory(categoryId) {
    if (destroyed) return;
    const cat = getCategories().find((c) => c.id === categoryId);
    if (!cat) return;
    await Promise.allSettled(cat.items.map((item) => preloadItem(item.id)));
  }

  /**
   * Play an item's audio sequence. When verbal descriptions are on,
   * plays intro-then-sound; otherwise just the sound. Any currently
   * playing audio is interrupted.
   *
   * Resolves when the last clip finishes, OR when a newer play()/stop()
   * call supersedes this one (so awaiters don't hang forever).
   *
   * @param {string} itemId
   * @param {{ animalStartGate?: Promise<void> }} [opts]
   *   animalStartGate: optional promise that must resolve before the
   *   ANIMAL sound (the final clip in the sequence) can begin. Used
   *   by animation-manager to hold back the animal until the button
   *   has finished its grow animation. Gating only applies to the
   *   last clip — the intro voice (when verbal descriptions are on)
   *   plays immediately alongside the grow, matching iOS. If the
   *   intro is longer than the grow the gate is already resolved by
   *   the time intro ends, so the animal plays immediately on
   *   intro-end; if the intro is shorter, the animal waits for the
   *   gate to resolve (leaving a brief silent gap).
   */
  async function play(itemId, opts = {}) {
    if (destroyed) return;

    dbg('[sound-manager] play() called for', itemId, 'unlocked =', unlocked);

    // Cancel anything currently playing — implemented as a monotonic
    // token bump. In-flight sequences will bail out at their next
    // await boundary.
    backend.stop();
    const myToken = ++playbackToken;

    // Loading is lazy: if the item hasn't been preloaded yet, fetch
    // it on demand. The await here may yield for a noticeable time
    // on cold cache; that's the documented lazy-load behaviour.
    if (!cache.has(itemId)) {
      dbg('[sound-manager] play() lazy-loading', itemId);
      try {
        await preloadItem(itemId);
      } catch (err) {
        // Loading failure is logged but swallowed. The child gets
        // no sound, but the app does not crash or surface an error
        // dialog.
        console.warn(`[sound-manager] failed to load ${itemId}:`, err);
        return;
      }
    }
    if (myToken !== playbackToken || destroyed) return;

    const entry = cache.get(itemId);
    if (!entry) {
      dbg('[sound-manager] play() cache miss after preload for', itemId);
      return;
    }

    // Build the sequence under the CURRENT setting. Intentionally
    // re-read on every play so a user toggling the setting mid-
    // session sees it take effect on the next tap.
    const sequence = [];
    if (getVerbalDescriptions() && entry.intro) {
      sequence.push(entry.intro);
    }
    sequence.push(entry.sound);
    dbg('[sound-manager] play() sequence length =', sequence.length, 'for', itemId);

    const gate = opts.animalStartGate;
    for (let i = 0; i < sequence.length; i++) {
      if (myToken !== playbackToken || destroyed) return;

      // Gate the animal sound (always the LAST clip in the sequence)
      // on the caller-supplied start gate. If the intro played first
      // and its duration >= the gate's timeout, this await returns
      // immediately; otherwise we block here until the gate fires.
      // Then we re-check the token because the gate wait could have
      // been superseded by a rapid second tap while we were waiting.
      if (gate && i === sequence.length - 1) {
        dbg('[sound-manager] play() awaiting animalStartGate for', itemId);
        try {
          await gate;
        } catch (err) {
          console.warn('[sound-manager] animalStartGate rejected:', err);
        }
        if (myToken !== playbackToken || destroyed) return;
        dbg('[sound-manager] play() animalStartGate resolved for', itemId);
      }

      const handle = sequence[i];
      try {
        dbg('[sound-manager] play() awaiting backend.play for', itemId);
        await backend.play(handle);
        dbg('[sound-manager] play() backend.play resolved for', itemId);
      } catch (err) {
        // A failed play mid-sequence aborts the rest of the sequence
        // but does not throw to the caller.
        console.warn(`[sound-manager] playback error on ${itemId}:`, err);
        return;
      }
      if (myToken !== playbackToken || destroyed) return;
    }
    dbg('[sound-manager] play() complete for', itemId);
  }

  function stop() {
    playbackToken++;
    backend.stop();
  }

  function destroy() {
    destroyed = true;
    playbackToken++;
    try {
      backend.stop();
    } catch {
      /* backend may already be torn down */
    }
    cache.clear();
    inflight.clear();
  }

  return {
    unlock,
    play,
    stop,
    preloadCategory,
    preloadItem,
    isUnlocked: () => unlocked,
    destroy,
    // Test-only — exposed so the suite can assert cache state without
    // having to peek through backend internals.
    __cache: cache,
  };
}

// ---------------------------------------------------------------------------
// Default backend selection
// ---------------------------------------------------------------------------

/**
 * Pick the best available backend for the current environment.
 * Web Audio is preferred for low-latency playback and precise
 * scheduling. If it isn't available (old browsers, jsdom test
 * environment), fall back to HTMLAudioElement which gives us
 * correct-enough behavior for this app's use case.
 */
export function selectDefaultBackend() {
  if (typeof window !== 'undefined') {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (typeof Ctx === 'function') {
      return createWebAudioBackend(Ctx);
    }
    if (typeof window.Audio === 'function') {
      return createHtmlAudioBackend();
    }
  }
  // Last-resort no-op backend so importing this module never crashes
  // in a non-browser environment (e.g. SSR or headless scripts).
  return createNullBackend();
}

/**
 * Web Audio backend — decoded AudioBuffers played through a single
 * AudioContext. Precise cancellation via source.stop().
 *
 * Contract detail: play() returns a promise that resolves when the
 * clip ends naturally *or* when stop()/a new play() supersedes it.
 * The sound-manager's outer loop relies on this — if a superseded
 * play()'s promise hung forever, the outer async function would
 * block past its token check and never bail out of the sequence.
 */
export function createWebAudioBackend(CtxCtor) {
  const Ctx = CtxCtor ?? (window.AudioContext || window.webkitAudioContext);
  const ctx = new Ctx();
  dbg('[webaudio] context created, state =', ctx.state, 'sampleRate =', ctx.sampleRate);
  /** @type {{ source: AudioBufferSourceNode, resolve: () => void } | null} */
  let current = null;

  /** Force-resolve the currently-playing entry (if any) without waiting
   * for the 'ended' event, so the caller's await backend.play() can
   * unblock immediately. */
  function abortCurrent() {
    if (!current) return;
    const old = current;
    current = null;
    try {
      // Clear onended first so the async 'ended' event fired by
      // stop() doesn't try to resolve a second time.
      old.source.onended = null;
      old.source.stop(0);
    } catch {
      /* already stopped */
    }
    old.resolve();
  }

  return {
    async unlock() {
      // Browsers keep newly-created AudioContexts in "suspended" state
      // until a user gesture resumes them.
      //
      // iOS Safari note: ctx.resume() alone is NOT sufficient on iOS.
      // The audio output hardware only actually wakes up when a
      // BufferSource is started inside the user-gesture call stack.
      // We play a 1-frame silent buffer to satisfy that requirement.
      // This is the canonical iOS Web Audio unlock pattern used by
      // every mature audio library (Howler, Tone.js, etc.).
      //
      // The silent-buffer start() MUST run synchronously in the
      // gesture frame — before any `await`. All code up to the first
      // await in this function body still counts as gesture-sync,
      // even though we've crossed into an async function.
      dbg('[webaudio] unlock() called, state =', ctx.state);
      if (ctx.state === 'suspended') {
        try {
          const silent = ctx.createBuffer(1, 1, 22050);
          const src = ctx.createBufferSource();
          src.buffer = silent;
          src.connect(ctx.destination);
          src.start(0);
          dbg('[webaudio] silent-buffer started');
        } catch (err) {
          console.warn('[webaudio] silent-buffer unlock failed:', err);
        }
        await ctx.resume();
        dbg('[webaudio] ctx.resume() done, state =', ctx.state);
      }
    },

    async load(url) {
      dbg('[webaudio] load() fetching', url);
      const response = await fetch(url);
      dbg('[webaudio] load() response', url, response.status, response.headers.get('content-type'));
      if (!response.ok) {
        throw new Error(`fetch ${url} -> ${response.status}`);
      }
      const arrayBuffer = await response.arrayBuffer();
      dbg('[webaudio] load() got', arrayBuffer.byteLength, 'bytes for', url);
      // decodeAudioData returns a Promise in modern browsers but the
      // callback form is the one that works everywhere including
      // older Safari.
      return new Promise((resolve, reject) => {
        ctx.decodeAudioData(
          arrayBuffer,
          (decoded) => {
            dbg(
              '[webaudio] decoded',
              url,
              'duration =',
              decoded.duration,
              'channels =',
              decoded.numberOfChannels,
            );
            resolve(decoded);
          },
          (err) => {
            console.warn('[webaudio] decodeAudioData failed for', url, err);
            reject(err);
          },
        );
      });
    },

    async play(buffer) {
      // Stop any in-flight source first. Without this, two rapid
      // play() calls can briefly overlap, and the previous play()
      // promise would hang past its sound-manager token check.
      abortCurrent();
      dbg(
        '[webaudio] play() state =',
        ctx.state,
        'buffer duration =',
        buffer ? buffer.duration : '(null)',
      );

      // iOS Safari suspends the AudioContext when the tab goes to the
      // background. When the user returns and taps a button, we're
      // still inside a user-gesture call stack, so ctx.resume() is
      // allowed. Without this, source.start() queues the audio but it
      // never plays, the 'ended' event never fires, and the play
      // promise hangs until the animation timeout.
      if (ctx.state === 'suspended') {
        dbg('[webaudio] play() context suspended — resuming');
        try {
          await ctx.resume();
          dbg('[webaudio] play() context resumed, state =', ctx.state);
        } catch (err) {
          console.warn('[webaudio] play() resume failed:', err);
        }
      }

      return new Promise((resolve) => {
        const source = ctx.createBufferSource();
        source.buffer = buffer;
        source.connect(ctx.destination);
        const entry = { source, resolve };
        source.onended = () => {
          dbg('[webaudio] source.onended fired');
          if (current === entry) {
            current = null;
            resolve();
          }
        };
        current = entry;
        try {
          source.start(0);
          dbg('[webaudio] source.start(0) returned');
        } catch (err) {
          // Start can throw if the buffer is invalid or the context
          // is closed; resolve rather than rejecting so the
          // sequence moves on cleanly.
          console.warn('[sound-manager] source.start threw:', err);
          if (current === entry) current = null;
          resolve();
        }
      });
    },

    stop() {
      abortCurrent();
    },
  };
}

/**
 * HTMLAudioElement backend — used as a fallback when Web Audio isn't
 * available. Simpler but higher latency and no precise scheduling.
 *
 * Contract detail: matches createWebAudioBackend — stop() and a new
 * play() force-resolve the previous play() promise so the outer
 * sound-manager loop can bail out past its token check.
 */
export function createHtmlAudioBackend() {
  /** @type {{ el: HTMLAudioElement, resolve: () => void, detach: () => void } | null} */
  let current = null;

  /** Force-resolve the current play() without waiting for the 'ended'
   * event. Pausing an <audio> does NOT fire 'ended', so we have to
   * resolve the promise manually. */
  function abortCurrent() {
    if (!current) return;
    const old = current;
    current = null;
    old.detach();
    try {
      old.el.pause();
      old.el.currentTime = 0;
    } catch {
      /* ignore */
    }
    old.resolve();
  }

  return {
    async unlock() {
      // Playing (and immediately pausing) a short silent clip on the
      // unlock gesture primes iOS Safari's audio permission. We use
      // a 1-sample silent WAV data URL so there's no extra asset to
      // ship.
      const silent = new Audio(SILENT_WAV);
      try {
        await silent.play();
      } catch {
        /* some browsers still reject; that's fine — the real play()
         * will have the same user-gesture stamp. */
      }
      silent.pause();
    },

    load(url) {
      return new Promise((resolve, reject) => {
        const el = new Audio();
        el.preload = 'auto';
        const onReady = () => {
          cleanup();
          resolve(el);
        };
        const onError = () => {
          cleanup();
          reject(new Error(`load failed: ${url}`));
        };
        const cleanup = () => {
          el.removeEventListener('canplaythrough', onReady);
          el.removeEventListener('loadeddata', onReady);
          el.removeEventListener('error', onError);
        };
        el.addEventListener('canplaythrough', onReady);
        // Fallback event for browsers/jsdom that never fire
        // canplaythrough for cached files.
        el.addEventListener('loadeddata', onReady);
        el.addEventListener('error', onError);
        el.src = url;
      });
    },

    play(el) {
      abortCurrent();

      return new Promise((resolve) => {
        const onEnded = () => {
          detach();
          if (current && current.el === el) {
            current = null;
          }
          resolve();
        };
        const onError = () => {
          detach();
          if (current && current.el === el) current = null;
          resolve();
        };
        const detach = () => {
          el.removeEventListener('ended', onEnded);
          el.removeEventListener('error', onError);
        };
        el.addEventListener('ended', onEnded);
        el.addEventListener('error', onError);

        current = { el, resolve, detach };

        try {
          el.currentTime = 0;
        } catch {
          /* some browsers throw if the media isn't ready yet */
        }
        const p = el.play();
        if (p && typeof p.catch === 'function') {
          p.catch(() => {
            // Play rejected — resolve to keep the sequence moving.
            detach();
            if (current && current.el === el) current = null;
            resolve();
          });
        }
      });
    },

    stop() {
      abortCurrent();
    },
  };
}

/** Minimal backend that drops everything — used in non-browser contexts. */
function createNullBackend() {
  return {
    async unlock() {},
    async load() {
      return null;
    },
    async play() {},
    stop() {},
  };
}

// 44-byte 8-bit mono silent WAV, base64-encoded. Used to prime iOS
// audio permission without shipping an extra asset.
const SILENT_WAV =
  'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=';
